import {
  MessagingChannelReplyMode,
  MessagingChannelType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import {
  isMessagingChannelTargetReference,
  MessagingChannel,
  principalDisplayName,
} from '~hooks/useMessaging/models';
import { Nullable } from '~types/Nullable';

const CHANNEL_TYPE_LABEL_KEYS: Record<MessagingChannelType, string> = {
  [MessagingChannelType.BROADCAST]: 'messaging.channel_types.broadcast',
  [MessagingChannelType.DIRECT]: 'messaging.channel_types.direct',
  [MessagingChannelType.JOB]: 'messaging.channel_types.job',
  [MessagingChannelType.ORDER]: 'messaging.channel_types.order',
};

export const messagingChannelTypeLabel = (channelType: MessagingChannelType) =>
  t(CHANNEL_TYPE_LABEL_KEYS[channelType]);

/** The rail filter names a set of conversations, so it reads plural. */
const CHANNEL_TYPE_FILTER_LABEL_KEYS: Record<MessagingChannelType, string> = {
  [MessagingChannelType.BROADCAST]: 'messaging.channel_type_filters.broadcasts',
  [MessagingChannelType.DIRECT]: 'messaging.channel_type_filters.direct_messages',
  [MessagingChannelType.JOB]: 'messaging.channel_type_filters.jobs',
  [MessagingChannelType.ORDER]: 'messaging.channel_type_filters.orders',
};

export const messagingChannelTypeFilterLabel = (channelType: MessagingChannelType) =>
  t(CHANNEL_TYPE_FILTER_LABEL_KEYS[channelType]);

const REPLY_MODE_LABEL_KEYS: Record<MessagingChannelReplyMode, string> = {
  [MessagingChannelReplyMode.FORUM]: 'messaging.reply_modes.forum',
  [MessagingChannelReplyMode.ONE_WAY]: 'messaging.reply_modes.one_way',
  [MessagingChannelReplyMode.REPLY_TO_SENDER]: 'messaging.reply_modes.reply_to_sender',
};

export const messagingReplyModeLabel = (replyMode: MessagingChannelReplyMode) =>
  t(REPLY_MODE_LABEL_KEYS[replyMode]);

/** The full labels are sentences; a tag beside a row title has room for two words. */
const REPLY_MODE_TAG_KEYS: Record<MessagingChannelReplyMode, string> = {
  [MessagingChannelReplyMode.FORUM]: 'messaging.reply_mode_tags.forum',
  [MessagingChannelReplyMode.ONE_WAY]: 'messaging.reply_mode_tags.one_way',
  [MessagingChannelReplyMode.REPLY_TO_SENDER]:
    'messaging.reply_mode_tags.reply_to_sender',
};

export const messagingReplyModeTag = (replyMode: MessagingChannelReplyMode) =>
  t(REPLY_MODE_TAG_KEYS[replyMode]);

/** An unnamed channel falls back to the counterpart, then to the channel type. */
export const messagingChannelTitle = (channel: MessagingChannel) => {
  const channelName = channel.name?.trim();
  if (channelName) {
    return channelName;
  }

  const { target } = channel;
  if (target && !isMessagingChannelTargetReference(target)) {
    const participantName = principalDisplayName(target)?.trim();
    if (participantName) {
      return participantName;
    }
  }

  return messagingChannelTypeLabel(channel.channelType);
};

/**
 * A broadcast quoted on a reply is named by the sender, who may not have named it. Takes the
 * name rather than a channel: the quote arrives as its own shape, not as a full channel.
 */
export const messagingBroadcastTitle = (name: Nullable<string>) =>
  name?.trim() || t('messaging.announcement');

/** Direct and job conversations hold only two people, so a reply mode says nothing there. */
const showsReplyMode = (channel: MessagingChannel) =>
  channel.channelType !== MessagingChannelType.DIRECT &&
  channel.channelType !== MessagingChannelType.JOB;

export const listsMessagingChannelMembers = (channel: MessagingChannel) =>
  channel.channelType === MessagingChannelType.ORDER ||
  channel.channelType === MessagingChannelType.BROADCAST;

const messagingChannelMemberCountLabel = (channel: MessagingChannel) =>
  channel.recipientCount > 0
    ? t('messaging.people_count', { count: channel.members.length })
    : null;

const joinSubtitleParts = (parts: Nullable<string>[]) =>
  parts.filter(Boolean).join(' · ');

export const messagingChannelSubtitle = (channel: MessagingChannel) => {
  const typeLabel = messagingChannelTypeLabel(channel.channelType);

  if (!showsReplyMode(channel)) {
    return typeLabel;
  }

  return joinSubtitleParts([
    typeLabel,
    messagingChannelMemberCountLabel(channel),
    messagingReplyModeTag(channel.replyMode),
  ]);
};

export const messagingTargetChannelSubtitle = (
  context: string,
  channel: Nullable<MessagingChannel>,
) =>
  channel && listsMessagingChannelMembers(channel)
    ? joinSubtitleParts([context, messagingChannelMemberCountLabel(channel)])
    : context;

/**
 * A direct conversation is titled by its counterpart and carries no reply mode, so it
 * needs no tag beyond its type.
 */
export const messagingChannelTags = (channel: MessagingChannel) => {
  if (channel.channelType === MessagingChannelType.DIRECT) {
    return [messagingChannelTypeLabel(channel.channelType)];
  }

  // A broadcast is announced rather than discussed, so it is labelled as such.
  const tags = [
    channel.channelType === MessagingChannelType.BROADCAST
      ? t('messaging.announcement')
      : messagingChannelTypeLabel(channel.channelType),
  ];
  const soleRecipientName = channel.soleRecipientName;

  if (soleRecipientName) {
    tags.push(soleRecipientName);
  } else {
    const memberCountLabel = messagingChannelMemberCountLabel(channel);
    if (memberCountLabel) {
      tags.push(memberCountLabel);
    }
  }

  if (showsReplyMode(channel)) {
    tags.push(messagingReplyModeTag(channel.replyMode));
  }

  return tags;
};
